import React, { useState } from 'react';
import { X, Check, ArrowRight, Building2, Utensils, Home, Sparkles, Dumbbell, Users, GraduationCap, Heart, Quote, Star, Phone } from 'lucide-react';
import { marches } from '../data/mock';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';
import BasqueFlag from './BasqueFlag';

const iconMap = {
  hotel: Building2,
  restaurant: Utensils,
  airbnb: Home,
  spa: Sparkles,
  sport: Dumbbell,
  home: Users,
  student: GraduationCap,
  health: Heart
};

const MarcheModal = ({ marche, onClose }) => {
  if (!marche) return null;
  const IconComponent = iconMap[marche.icon];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with image */}
        <div className="relative h-56 md:h-72">
          <img
            src={marche.image}
            alt={marche.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/40 transition-colors"
          >
            <X size={24} />
          </button>
          
          {/* Title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: marche.color }}>
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-white">{marche.title}</h2>
                <p className="text-white/80">{marche.shortDesc}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-8">
          {/* Intro */}
          <p className="text-lg text-gray-700 leading-relaxed">
            {marche.details.intro}
          </p>

          {/* Grid: Pourquoi + Avantages */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Pourquoi */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Star size={20} className="text-[#9B1B30]" />
                Pourquoi nous choisir
              </h3>
              <ul className="space-y-3">
                {marche.details.pourquoi.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#006633] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={12} className="text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Avantages */}
            <div className="bg-[#9B1B30]/5 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles size={20} className="text-[#9B1B30]" />
                Vos avantages exclusifs
              </h3>
              <ul className="space-y-3">
                {marche.details.avantages.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#9B1B30] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={12} className="text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 relative text-white">
            <Quote className="absolute top-4 right-4 w-10 h-10 text-white/10" />
            <p className="text-white/90 italic text-lg mb-3">
              "{marche.details.testimonial}"
            </p>
            <p className="text-[#9B1B30] font-semibold text-sm">— {marche.details.author}</p>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="tel:0620254739"
              className="flex-1 flex items-center justify-center gap-3 bg-[#9B1B30] text-white px-6 py-4 rounded-xl font-bold hover:bg-[#B91C3B] transition-colors shadow-lg"
            >
              <Phone size={20} />
              Nous contacter
            </a>
            <a
              href="#tarifs"
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-900 px-6 py-4 rounded-xl font-bold hover:bg-gray-200 transition-colors"
            >
              Voir les tarifs
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const Marches = () => {
  const [selectedMarche, setSelectedMarche] = useState(null);

  return (
    <section id="marches" className="section-padding bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#9B1B30]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#006633]/10 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative">
        {/* Header */}
        <AnimateOnScroll animation="fadeUp">
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 bg-[#9B1B30] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Building2 size={16} />
              <span>Offres par secteur</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6">
              Une offre adaptée à <span className="text-[#9B1B30]">votre activité</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
              Cliquez sur votre secteur pour découvrir vos avantages exclusifs 
              et réductions jusqu'à <strong className="text-[#9B1B30]">-30%</strong>.
            </p>
          </div>
        </AnimateOnScroll>

        {/* Marches grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {marches.map((marche, index) => {
            const IconComponent = iconMap[marche.icon];
            return (
              <AnimateOnScroll key={marche.id} animation="fadeUp" delay={index * 50}>
                <button
                  onClick={() => setSelectedMarche(marche)}
                  className="card-hover group bg-white/5 backdrop-blur-sm rounded-2xl p-6 text-left relative overflow-hidden border border-white/10 hover:border-[#9B1B30]/50 w-full h-full"
                >
                  {/* Hover glow */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity rounded-2xl"
                    style={{ backgroundColor: marche.color }}
                  />
                  
                  {/* Icon */}
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${marche.color}20` }}
                  >
                    <IconComponent className="w-7 h-7" style={{ color: marche.color }} />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#9B1B30] transition-colors">
                    {marche.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {marche.shortDesc}
                  </p>
                  
                  {/* Arrow */}
                  <div className="flex items-center gap-2 text-[#9B1B30] font-semibold text-sm">
                    <span>Découvrir</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              </AnimateOnScroll>
            );
          })}
        </div>

        {/* Bottom banner */}
        <AnimateOnScroll animation="fadeUp">
          <div className="mt-16 bg-gradient-to-r from-[#006633] to-[#008844] rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
            <div className="flex items-center gap-5 text-center md:text-left">
              <BasqueFlag size={60} />
              <div className="text-white">
                <h4 className="text-xl md:text-2xl font-bold">Une laverie ancrée à Bayonne</h4>
                <p className="text-white/80">Au service des professionnels et particuliers du Pays Basque</p>
              </div>
            </div>
            <a
              href="tel:0620254739"
              className="bg-white text-[#006633] px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg flex items-center gap-2"
            >
              <Phone size={20} />
              Contactez-nous
            </a>
          </div>
        </AnimateOnScroll>
      </div>

      {/* Modal */}
      {selectedMarche && (
        <MarcheModal 
          marche={selectedMarche} 
          onClose={() => setSelectedMarche(null)} 
        />
      )}
    </section>
  );
};

export default Marches;
